/**
 * May this user create/edit contractors and framework agreements?
 * Mirrors backend `User.has_manage_contractors_permission` / `CanManageContractors`:
 * CEO and technical directors by role, anyone else internal only with the
 * CEO-granted flag. Falls back to the raw flag for sessions persisted before the
 * computed permission existed.
 */
export function canManageContractors(
  user:
    | {
        role?: string;
        is_ceo?: boolean;
        is_technical_director?: boolean;
        can_manage_contractors?: boolean;
        has_manage_contractors_permission?: boolean;
      }
    | null
    | undefined
): boolean {
  if (!user) return false;
  if (user.role === "contractor") return false;
  if (user.has_manage_contractors_permission !== undefined)
    return !!user.has_manage_contractors_permission;
  if (user.is_ceo) return true;
  if (user.role === "director" && user.is_technical_director) return true;
  return !!user.can_manage_contractors;
}

/**
 * May this user open the contractors megamap (/contractors/megamapa)? The contractors
 * panel circle (ContractorsAccessGuard: directors, coordinators, the 'Podwykonawcy i umowy
 * ramowe' grant) plus developers (is_developer) — for them ONLY the map, not the rest of
 * the panel. Backend data routes need just an internal account.
 */
/**
 * May this user open the contractors panel (list and contractor cards under /contractors)?
 * Mirrors routes ContractorsAccessGuard: directors, coordinators and holders of the
 * 'Podwykonawcy i umowy ramowe' grant. Developers see ONLY the megamap, so the map hides
 * the card link from them (the guard would bounce them to the dashboard anyway).
 */
export function canAccessContractorsPanel(
  user: Parameters<typeof canManageContractors>[0] | null | undefined,
): boolean {
  if (!user || user.role === "contractor") return false;
  if (user.role === "director" || user.role === "coordinator") return true;
  return canManageContractors(user);
}

export function canOpenContractorAreaMap(
  user:
    | (Parameters<typeof canManageContractors>[0] & { is_developer?: boolean })
    | null
    | undefined
): boolean {
  if (!user || user.role === "contractor") return false;
  return !!user.is_developer || canAccessContractorsPanel(user);
}

/**
 * May this user deactivate / reactivate a contractor and filter the inactive ones on
 * the list? Mirrors backend `IsTechnicalDirector` (ContractorViewSet._can_toggle_active):
 * CEO, or a director with is_technical_director. Contractors are never deleted —
 * deactivation replaces it — so the 'Podwykonawcy i umowy ramowe' grant alone is
 * not enough.
 */
export function canToggleContractorActive(
  user:
    | {
        role?: string;
        is_ceo?: boolean;
        is_technical_director?: boolean;
      }
    | null
    | undefined
): boolean {
  if (!user) return false;
  if (user.is_ceo) return true;
  return user.role === "director" && !!user.is_technical_director;
}

/**
 * May this user create agreement groups and move framework agreements between them?
 * Mirrors backend `User.has_manage_agreement_groups_permission` /
 * `CanManageAgreementGroups`: CEO and technical directors by role, anyone else
 * internal only with the CEO-granted „Grupy umów ramowych" flag. Deliberately NOT
 * implied by canManageContractors — grouping steers subcontractor selection in
 * estimating, so it is a separate, narrower grant.
 */
export function canManageAgreementGroups(
  user:
    | {
        role?: string;
        is_ceo?: boolean;
        is_technical_director?: boolean;
        can_manage_agreement_groups?: boolean;
        has_manage_agreement_groups_permission?: boolean;
      }
    | null
    | undefined
): boolean {
  if (!user) return false;
  if (user.role === "contractor") return false;
  if (user.has_manage_agreement_groups_permission !== undefined)
    return !!user.has_manage_agreement_groups_permission;
  if (user.is_ceo) return true;
  if (user.role === "director" && user.is_technical_director) return true;
  return !!user.can_manage_agreement_groups;
}

export type EntityType = "company" | "individual";

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  company: "Firma",
  individual: "Osoba fizyczna",
};

export interface ContractorLike {
  entity_type?: EntityType;
  name?: string | null;
  short_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  nip?: string | null;
  pesel?: string | null;
  display_name?: string | null;
  tax_identifier?: string | null;
  tax_identifier_label?: "NIP" | "PESEL" | null;
  document_label?: "Faktura" | "Rachunek" | null;
}

export function getContractorDisplayName(c: ContractorLike | null | undefined): string {
  if (!c) return "";
  if (c.display_name) return c.display_name;
  if (c.entity_type === "individual") {
    return `${c.last_name ?? ""} ${c.first_name ?? ""}`.trim();
  }
  return c.name ?? "";
}

export function getContractorTaxId(
  c: ContractorLike | null | undefined
): { label: "NIP" | "PESEL"; value: string } {
  const isIndividual = c?.entity_type === "individual";
  return {
    label: (c?.tax_identifier_label as "NIP" | "PESEL") ?? (isIndividual ? "PESEL" : "NIP"),
    value: c?.tax_identifier ?? (isIndividual ? c?.pesel ?? "" : c?.nip ?? ""),
  };
}

export function getInvoiceDocumentLabel(
  c: ContractorLike | null | undefined
): "Faktura" | "Rachunek" {
  if (c?.document_label) return c.document_label;
  return c?.entity_type === "individual" ? "Rachunek" : "Faktura";
}

const PESEL_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];

export function validatePeselClient(pesel: string): boolean {
  if (typeof pesel !== "string" || pesel.length !== 11 || !/^\d{11}$/.test(pesel)) {
    return false;
  }
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(pesel[i], 10) * PESEL_WEIGHTS[i];
  }
  const check = (10 - (sum % 10)) % 10;
  return check === parseInt(pesel[10], 10);
}

/**
 * May this user see (and edit) „Zasady podpowiadania podwykonawców"? Mirrors the backend
 * gate on /contractors/knowledge-notes/ (CanManageContractors): CEO, a technical director
 * or the 'Podwykonawcy i umowy ramowe' grant — the rules are an assessment of companies,
 * so everyone outside that circle sees only the rule numbers in a suggestion, never the text.
 */
export function canAccessContractorKnowledge(
  user:
    | {
        role?: string;
        is_ceo?: boolean;
        is_technical_director?: boolean;
        can_manage_contractors?: boolean;
        has_manage_contractors_permission?: boolean;
      }
    | null
    | undefined
): boolean {
  // Od 2026-10-19 ten sam krąg, co „Podwykonawcy i umowy ramowe" (backend:
  // CanManageContractors na /contractors/knowledge-notes/ i treść zasad w podpowiedzi).
  return canManageContractors(user);
}
